import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import { appRoutes, ROUTE_PATH_FORBIDDEN } from './app.routes';
import {
  AzureConfig,
  MsalGuardConfig,
  MsalInstanceConfig,
  MsalInterceptorConfig,
  ProtectedResource,
  SharedAzureAuthModule,
} from '@schaeffler/azure-auth';
import { environment } from '../environments/environment';
import { StopCodesAuthInterceptor } from './shared/interceptors/stop-codes-auth.interceptor';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideTranslocoActiveLanguage } from './shared/i18n/transloco.providers';
import { provideTranslocoRoot } from './shared/i18n/transloco-root.providers';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { MSAL_INSTANCE, MsalService } from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { firstValueFrom, of } from 'rxjs';
import { catchError, defaultIfEmpty } from 'rxjs/operators';

const AVAILABLE_LANGS = ['en', 'de'] as const;
const FALLBACK_LANGUAGE = 'en';
const LANGUAGE_STORAGE_KEY = 'language';

/**
 * Handles the OAuth redirect callback on application startup.
 *
 * MSAL performs its login via a redirect flow and returns control to the SPA with
 * a callback URL. This initializer ensures that MSAL processes that callback
 * before the router performs the initial navigation (blocking initial navigation
 * is enabled via `withEnabledBlockingInitialNavigation()`).
 *
 * This function must complete (resolve) quickly and must not throw; failures are
 * caught and logged to keep the app navigable.
 */
const handleMsalRedirectOnStartup = async (): Promise<void> => {
  const msalService = inject(MsalService);

  await firstValueFrom(
    msalService.handleRedirectObservable().pipe(
      defaultIfEmpty(null),
      catchError((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error('[auth] MSAL redirect handling failed', error);
        return of(null);
      }),
    ),
  );
};

const azureConfig = new AzureConfig(
  new MsalInstanceConfig(
    environment.clientId,
    environment.tenantId,
    !environment.production,
  ),
  new MsalInterceptorConfig([
    new ProtectedResource('/api/*', [environment.scope]),
    new ProtectedResource('/dotnet/api/*', [environment.stopCodesScope]),
    ...(environment.stopCodesApiBaseUrl &&
    !environment.stopCodesApiBaseUrl.includes('localhost')
      ? [
          new ProtectedResource(
            `${environment.stopCodesApiBaseUrl.replace(/\/$/, '')}${(environment.stopCodesApiPath ?? '/dotnet/api').replace(/\/$/, '')}/*`,
            [environment.stopCodesScope],
          ),
        ]
      : []),
  ]),
  new MsalGuardConfig(`/${ROUTE_PATH_FORBIDDEN}`, [environment.scope]),
);

const msalInstanceFactory = (): IPublicClientApplication => {
  const { msalInstance } = azureConfig;
  const isLegacyIEBrowser =
    globalThis.navigator.userAgent.includes('MSIE ') ||
    globalThis.navigator.userAgent.includes('Trident/');

  return new PublicClientApplication({
    auth: {
      clientId: msalInstance.clientId,
      authority: `${msalInstance.loginUrl}${msalInstance.tenantId}/`,
      redirectUri: msalInstance.redirectUri,
      postLogoutRedirectUri: msalInstance.postLogoutRedirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: environment.persistAuthAcrossSessions
        ? BrowserCacheLocation.LocalStorage
        : BrowserCacheLocation.SessionStorage,
      storeAuthStateInCookie: isLegacyIEBrowser,
    },
    system: {
      allowRedirectInIframe: msalInstance.allowRedirectInIframe,
      loggerOptions: msalInstance.loggingEnabled
        ? {
            /* eslint-disable no-console */
            loggerCallback: (level: LogLevel, message: string) => {
              const tag = '[MSAL]';
              if (level === LogLevel.Error) console.error(tag, message);
              else if (level === LogLevel.Warning) console.warn(tag, message);
              else if (level === LogLevel.Info) console.info(tag, message);
              else console.debug(tag, message);
            },
            /* eslint-enable no-console */
            piiLoggingEnabled: false,
          }
        : undefined,
    },
  });
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(handleMsalRedirectOnStartup),
    provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
    provideTranslocoActiveLanguage({
      availableLangs: AVAILABLE_LANGS,
      fallbackLang: FALLBACK_LANGUAGE,
      storageKey: LANGUAGE_STORAGE_KEY,
    }),
    ...provideTranslocoRoot({
      production: environment.production,
      availableLangs: AVAILABLE_LANGS,
      defaultLang: 'en',
      fallbackLang: FALLBACK_LANGUAGE,
    }),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      SharedAzureAuthModule.forRoot(azureConfig),
      EffectsModule.forRoot([]),
      StoreModule.forRoot({}),
      ...(environment.production
        ? []
        : [StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: false })]),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StopCodesAuthInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
    },
  ],
};
